import {
  cloneElement,
  isValidElement,
  useState,
  type ReactElement,
  type Ref,
} from 'react'
import { Bug, Lightbulb } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import PortalDropdownMenu, {
  type PortalDropdownPlacement,
} from '../ui/PortalDropdownMenu'
import { ClickUpFeedbackFormModal } from './ClickUpFeedbackFormModal'
import { useFeedback } from './FeedbackContext'
import { type FeedbackKind } from '../../lib/feedback/kinds'

const KINDS: FeedbackKind[] = ['bug', 'idea']

const KIND_ICON = {
  bug: Bug,
  idea: Lightbulb,
} as const

type TriggerProps = {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  ref?: Ref<HTMLButtonElement>
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
}

interface FeedbackKindMenuProps {
  trigger: ReactElement<TriggerProps>
  placement?: PortalDropdownPlacement
  anchorClassName?: string
  menuWidth?: number
}

/**
 * Quackback on: Bug / Idea chooser → widget `new-post` (legacy ClickUp still linked).
 * Quackback off: trigger opens ClickUp form only — no widget, no chooser.
 */
export function FeedbackKindMenu({
  trigger,
  placement = 'top-start',
  anchorClassName,
  menuWidth = 280,
}: FeedbackKindMenuProps) {
  const { t } = useTranslation('dashboard')
  const { isOpen, openFeedback, closeFeedback, quackbackEnabled } = useFeedback()
  const [menuOpen, setMenuOpen] = useState(false)
  const [legacyFormOpen, setLegacyFormOpen] = useState(false)

  if (!isValidElement(trigger)) {
    return null
  }

  if (!quackbackEnabled) {
    const clickUpTrigger = cloneElement(trigger, {
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onClick?.(event)
        setLegacyFormOpen(true)
      },
    })
    return (
      <>
        {anchorClassName ? (
          <div className={anchorClassName}>{clickUpTrigger}</div>
        ) : (
          clickUpTrigger
        )}
        <ClickUpFeedbackFormModal
          open={legacyFormOpen}
          onClose={() => setLegacyFormOpen(false)}
        />
      </>
    )
  }

  const enrichedTrigger = cloneElement(trigger, {
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event)
      if (isOpen) {
        event.preventDefault()
        closeFeedback()
        setMenuOpen(false)
      }
    },
    'aria-haspopup': 'menu',
    'aria-expanded': menuOpen || isOpen,
  })

  const pick = (kind: FeedbackKind) => {
    setMenuOpen(false)
    openFeedback(kind)
  }

  const openLegacyForm = () => {
    setMenuOpen(false)
    setLegacyFormOpen(true)
  }

  return (
    <>
      <PortalDropdownMenu
        open={menuOpen}
        onOpenChange={(next) => {
          if (isOpen) {
            setMenuOpen(false)
            return
          }
          setMenuOpen(next)
        }}
        placement={placement}
        menuWidth={menuWidth}
        anchorClassName={anchorClassName}
        menuClassName={[
          'z-[100] overflow-hidden rounded-xl border border-[#333]',
          'bg-[#1a1a1a] p-1.5 shadow-2xl shadow-black/50',
        ].join(' ')}
        trigger={enrichedTrigger}
      >
        <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-medium uppercase tracking-wide text-gray-500 font-switzer">
          {t('sidebar.feedback.chooserTitle')}
        </p>
        <div className="flex flex-col gap-1">
          {KINDS.map((kind) => {
            const Icon = KIND_ICON[kind]
            return (
              <button
                key={kind}
                type="button"
                role="menuitem"
                onClick={() => pick(kind)}
                className={[
                  'group flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left',
                  'border border-transparent',
                  'transition-colors',
                  'hover:border-[#404040] hover:bg-[#2a2a2a]',
                  'focus:outline-none focus-visible:border-[#FF5A00]/60 focus-visible:bg-[#2a2a2a]',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    'bg-[#0f0f0f] border border-[#333]',
                    'group-hover:border-[#FF5A00]/40',
                  ].join(' ')}
                >
                  <Icon size={17} strokeWidth={2} className="text-[#FF5A00]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-white font-switzer">
                    {t(`sidebar.feedback.kinds.${kind}.label`)}
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-gray-400 font-switzer">
                    {t(`sidebar.feedback.kinds.${kind}.description`)}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
        <div className="mt-1 border-t border-[#333] px-2.5 pb-1 pt-2">
          <button
            type="button"
            role="menuitem"
            onClick={openLegacyForm}
            className={[
              'w-full text-left text-xs text-gray-400 font-switzer',
              'underline-offset-2 hover:text-gray-200 hover:underline',
              'focus:outline-none focus-visible:text-gray-200 focus-visible:underline',
            ].join(' ')}
          >
            {t('sidebar.feedback.useOldForm')}
          </button>
        </div>
      </PortalDropdownMenu>

      <ClickUpFeedbackFormModal
        open={legacyFormOpen}
        onClose={() => setLegacyFormOpen(false)}
      />
    </>
  )
}
